import { bcLong, bcInt } from '@lib2/model/util/string/BaseConverter';
import { ServiceHosts } from '@lib2/model/common/ServiceHosts';
import type { ListType } from '@/lib/constants/playlist';
import { PLAYLIST_TYPE_IDS, MEDIA_TYPE_IDS } from '@/lib/constants/playlist';
import type { MediaType } from '@/lib/types/Media';

interface PlaylistEmbedParams {
  cId: number;
  playerId: number;
  listId: number;
  listType: ListType;
  mediaType: MediaType;
  isReseller: boolean;
}

interface PlaylistPlayerOptions {
  autoPlay?: boolean;
  preview?: boolean;
  loop?: boolean;
}

interface WidthHeight {
  width: number;
  height: number;
}

export class PlaylistLinker {

  /** Generate the embed/preview URL for a playlist player */
  static getPreviewUrl(
    params: PlaylistEmbedParams,
    options?: PlaylistPlayerOptions
  ): string {
    const hostname = params.isReseller
      ? ServiceHosts.content.reseller
      : ServiceHosts.content.streamhoster;
    const uriStem = PlaylistLinker.buildUriStem(params);
    const qs = PlaylistLinker.buildQueryString(options);
    return `https://${hostname}/embed/list/${uriStem}${qs}`;
  }

  /** Generate iframe HTML for embedding a playlist player */
  static getIframeHtml(
    params: PlaylistEmbedParams,
    isResponsive: boolean,
    widthHeight: WidthHeight,
    options?: PlaylistPlayerOptions
  ): string {
    const url = PlaylistLinker.getPreviewUrl(params, options);

    const iframeAttrs = `frameborder="0" scrolling="no" webkitallowfullscreen mozallowfullscreen allowfullscreen allow="autoplay; fullscreen;"`;

    if (isResponsive) {
      const paddingBottom = Math.round((widthHeight.height / widthHeight.width * 100) * 100) / 100;
      return (
        `<div style="position: relative; padding-bottom: ${paddingBottom}%; height: 0;">` +
        `<iframe src="${url}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" ${iframeAttrs}></iframe>` +
        `</div>`
      );
    }

    return `<iframe src="${url}" width="${widthHeight.width}" height="${widthHeight.height}" ${iframeAttrs}></iframe>`;
  }

  private static buildUriStem(params: PlaylistEmbedParams): string {
    const cIdBC = bcInt.getStr(params.cId);
    const playerIdBC = bcLong.getStr(params.playerId);
    const listIdBC = bcLong.getStr(params.listId);
    const listTypeId = PLAYLIST_TYPE_IDS[params.listType];
    const mediaTypeId = MEDIA_TYPE_IDS[params.mediaType];
    return `${cIdBC}/${playerIdBC}/${listIdBC}_${listTypeId}_${mediaTypeId}`;
  }

  private static buildQueryString(options?: PlaylistPlayerOptions): string {
    if (!options) return '';
    const pairs: string[] = [];
    if (options.autoPlay) pairs.push('autoPlay=1');
    if (options.preview) pairs.push('preview=1');
    if (options.loop) pairs.push('loop=1');
    return pairs.length > 0 ? `?${pairs.join('&')}` : '';
  }
}
